import { watch } from 'vue';

/**
 * A throttled version of Vue's watch function that limits how often the callback can be called.
 *
 * @param {any} source - The source to watch. Can be a ref, computed ref, or a getter function.
 * @param {Function} cb - The callback function to be called when the source changes.
 * @param {Object} options - Configuration options
 * @param {number} [options.throttle=0] - The minimum time (in milliseconds) that must pass between callback executions.
 * @param {Object} [options.watchOptions] - Additional options to pass to Vue's watch function.
 * @returns {Function} A function to stop watching.
 *
 * @example
 * // Basic usage with 500ms throttle
 * watchThrottled(
 *   () => someRef.value,
 *   (newValue, oldValue) => {
 *     console.log('Value changed:', newValue);
 *   },
 *   { throttle: 500 }
 * );
 *
 * @example
 * // Using with immediate option
 * watchThrottled(
 *   computedValue,
 *   (newValue) => {
 *     // This will run immediately and then throttle subsequent calls
 *     updateUI(newValue);
 *   },
 *   {
 *     throttle: 1000,
 *     immediate: true
 *   }
 * );
 */
export function watchThrottled(source, cb, options = {}) {
  const { throttle = 0, ...watchOptions } = options;

  let timeoutId = null;
  let lastArgs = null;
  let lastThis = null;

  const throttledCb = (...args) => {
    lastArgs = args;
    lastThis = this;
    if (timeoutId === null) {
      timeoutId = setTimeout(() => {
        cb.apply(lastThis, lastArgs);
        timeoutId = null;
        lastArgs = null;
        lastThis = null;
      }, throttle);
    }
  };

  if (throttle > 0) {
    return watch(source, throttledCb, watchOptions);
  }
  // If throttle is 0 or not provided, behave like a regular watch
  return watch(source, cb, watchOptions);
}
